import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IFilterPillIcon } from '~/src/dash/components/filters/common';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { SortByMode } from '~/src/dash/components/material-search-dropdown-multi-list';
import { Div, TKey, usePublicaTranslation } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';

const getSelectedFilters = (list: IMenuList) => {
  const result = {} as IMenuList;
  Object.values(list).forEach((item) => {
    if (item.selected) {
      result[item.ID] = item;
    }
  });
  return result;
};

interface IFilterPillProps {
  activateFilterClear: Record<string, boolean>; // this is a faux object we send to trigger a clear. ex: { clear: true }
  cypressParentName: string; // to be implemented after dropdown refactor
  defaultSelected?: string[]; // array of ids that are pre-selected // do not implement until user settings is complete
  handleFilterAccept: (menuTitle: string, list: IMenuList) => void;
  icon?: IFilterPillIcon; // icon info object
  list: IMenuList;
  menuTitle: string; // keep this plural ex: 'Countries', singularTitle ex: 'Country'
  singularTitle?: string; // see above
  sortMode?: SortByMode;
  isMultiSelectDisabled?: boolean;
  hasSelectAll?: boolean;
  tKey?: TKey;
}

export const FilterPill = ({
  defaultSelected,
  menuTitle,
  singularTitle,
  list,
  activateFilterClear,
  handleFilterAccept,
  sortMode,
  icon,
  isMultiSelectDisabled,
  hasSelectAll,
  tKey,
}: IFilterPillProps) => {
  const { t } = usePublicaTranslation();
  // declare states and refs
  const isMounted = useRef(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [storedList, setStoredList] = useState(list);
  const [hasListChanged, setHasListChanged] = useState(false);
  const numSelected = Object.values(storedList).filter((item) => item.selected).length;

  useEffect(() => {
    if (isMounted.current && activateFilterClear.clear) {
      const clonedList = cloneDeep(list);
      Object.keys(clonedList).forEach((key) => {
        clonedList[key].selected = false;
      });
      unstable_batchedUpdates(() => {
        setStoredList(clonedList);
      });
    }
  }, [activateFilterClear]);

  useEffect(() => {
    if (isMounted.current) {
      setHasListChanged(true);
      if (isMultiSelectDisabled) {
        handleFilterAccept(menuTitle, getSelectedFilters(storedList));
        setHasListChanged(false);
      }
    } else {
      if (!!defaultSelected && !isEmpty(defaultSelected)) {
        handleFilterAccept(menuTitle, getSelectedFilters(storedList));
        setHasListChanged(false);
      }
      isMounted.current = true;
    }
  }, [storedList]);

  const handleOnChange = (updatedList: IMenuList) => {
    unstable_batchedUpdates(() => {
      setStoredList(updatedList);
      setIsHovered(false);
    });
  };

  const handleAccept = () => {
    if (hasListChanged) {
      handleFilterAccept(menuTitle, getSelectedFilters(storedList));
      unstable_batchedUpdates(() => {
        setHasListChanged(false);
        setIsHovered(false);
      });
    }
  };

  return (
    <Div
      onHover={() => setIsHovered(true)}
      onLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: numSelected
          ? publicaColors.honeydew
          : isHovered
          ? publicaColors.lightGray
          : publicaColors.veryLightGray,
        borderRadius: '6px',
        padding: '3px 4px 5px 12px',
        margin: '2px 8px 2px 0px',
        cursor: 'pointer',
      }}
    >
      <MaterialSearchDropdownMulti
        pillIcon={icon}
        disabled={false}
        fontSize='12px'
        isFontBold
        fontColor={numSelected ? publicaColors.jungleGreen : publicaColors.black}
        menuTitle={tKey ? t(tKey) : menuTitle}
        singularTitle={singularTitle}
        list={storedList}
        onChange={handleOnChange}
        handleMenuAcceptClick={handleAccept}
        onlyShowNumberWhenClosed={true}
        maxHeight={320}
        selectAll={!!hasSelectAll}
        enableMultiSelect={!isMultiSelectDisabled}
        disableBottomBorder={true}
        overrideDefaultSortMode={sortMode}
        responsivePositionEnabled
      />
    </Div>
  );
};
